/**
 * ActionBuilder
 *
 * Renders a list of type-dropdown + dynamic parameter fields for rule actions.
 * All action definitions come from the backend catalog (/admin/rule-actions).
 *
 * Props:
 *   brandName       – current brand (used for remote selects)
 *   actionCatalog   – array of action definitions from /admin/rule-actions
 *   value           – Array of { type, params: {} } current actions
 *   onChange        – (actionsArray) => void
 *
 * Exports:
 *   buildActionsArray(actionsState) – converts the state into the [action] array
 *                                     expected by the backend
 *   parseActionsArray(arr)          – parses the saved array back into state
 */
import React, { useState, useEffect } from "react";
import {
    Box, Typography, TextField, MenuItem, CircularProgress,
    Chip, Grid, Autocomplete, IconButton, Button, Stack,
    ToggleButton, ToggleButtonGroup
} from "@mui/material";
import PinIcon from "@mui/icons-material/Pin";
import DataObjectIcon from "@mui/icons-material/DataObject";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { LOYALTY_API_BASE, SECTION_COLORS } from "../../constants";
import { loyaltyFetch } from "../../hooks/loyaltyFetch";

// ── Action type metadata (mirrors the backend catalog) ────────────────────────
const ACTION_META = {
    earn_points: { label: "Earn points", color: "#16a34a", bg: "#dcfce7" },
    burn_points: { label: "Burn points", color: "#dc2626", bg: "#fee2e2" },
    redeem_reward: { label: "Redeem reward", color: "#9333ea", bg: "#f3e8ff" },
    issue_reward: { label: "Issue reward", color: "#7c3aed", bg: "#ede9fe" },
    issue_coupon: { label: "Attribuer un coupon", color: "#ea580c", bg: "#fff7ed" },
    earn_points_from_amount: { label: "Earn points from amount", color: "#0369a1", bg: "#e0f2fe" },

    reset_status_points: { label: "Reset status points", color: "#475569", bg: "#f1f5f9" },
    downgrade_one_tier: { label: "Downgrade one tier", color: "#be185d", bg: "#fce7f3" },
    set_customer_status: { label: "Set customer status", color: "#0891b2", bg: "#e0f2fe" },
    set_rank: { label: "Set rank (tier)", color: "#c026d3", bg: "#fdf4ff" },
    add_customer_tag: { label: "Add customer tag", color: "#059669", bg: "#d1fae5" },
};

// ── Helper: build actions array for API body ──────────────────────────────────
export function buildActionsArray(actionsState) {
    if (!Array.isArray(actionsState) || actionsState.length === 0) return [];

    return actionsState.map(actionState => {
        if (!actionState || !actionState.type) return null;
        const obj = { type: actionState.type };
        const params = actionState.params || {};

        if (actionState.type === "issue_coupon") {
            if (!params.coupon_type_id) return null;
            const obj = { type: "issue_coupon", coupon_type_id: params.coupon_type_id };
            if (params.frequency) obj.frequency = params.frequency;
            const rewardMode = params._rewardMode || "all";
            if (rewardMode === "none") obj.reward_ids = [];
            else if (rewardMode === "pick") {
                const ids = Array.isArray(params.reward_ids)
                    ? params.reward_ids.filter(Boolean)
                    : (params.reward_ids ? [params.reward_ids] : []);
                if (ids.length > 0) obj.reward_ids = ids;
            }
            return obj;
        }

        // Handle earn_points dynamic mode
        if (actionState.type === "earn_points" && params._pointsMode === "dynamic") {
            if (params._dynamicPath === "productNames") {
                // Product catalogue mode: sum points via productNames[] × productQuantities[]
                obj.points = {
                    "$fn": "sum_product_points_unomi",
                    args: [
                        { "$path": "payload.productNames" },
                        { "$path": "payload.productQuantities" },
                    ],
                };
            } else {
                // Simple payload field (default: orderTotal)
                const pathField = (params._dynamicPath || "orderTotal").trim();
                const clean = pathField.replace(/^(transaction\.)?payload\./, "") || "orderTotal";
                obj.points = { $path: `payload.${clean}` };
            }
            const mul = Number(params.multiplier);
            if (!isNaN(mul) && mul > 0 && params.multiplier !== "") obj.multiplier = mul;
            return obj;
        }

        Object.entries(params).forEach(([k, v]) => {
            // skip internal UI-only keys
            if (k === "_pointsMode" || k === "_dynamicPath" || k === "_rewardMode") return;
            if (v !== "" && v !== null && v !== undefined) {
                // coerce number fields
                const numFields = ["points", "min_points", "max_points", "rate", "start_in_seconds", "multiplier"];
                if (numFields.includes(k)) {
                    const n = Number(v);
                    if (!isNaN(n)) obj[k] = n;
                } else {
                    obj[k] = v;
                }
            }
        });
        // special case: amount_path is now hidden but required by backend
        if (actionState.type === "earn_points_from_amount" && !obj.amount_path) {
            obj.amount_path = "amount";
        }
        return obj;
    }).filter(a => a !== null);
}

// ── Helper: parse actions array from API into state ───────────────────────────
export function parseActionsArray(actionsArr) {
    if (!Array.isArray(actionsArr) || actionsArr.length === 0) return [];
    return actionsArr.map(action => {
        const { type, ...rest } = action;
        if (type === "issue_coupon") {
            let _rewardMode = "all";
            if (Array.isArray(rest.reward_ids)) {
                _rewardMode = rest.reward_ids.length === 0 ? "none" : "pick";
            }
            return { type, params: { ...rest, _rewardMode } };
        }
        if (type === "earn_points" && rest.points && typeof rest.points === "object") {
            // $fn: sum_product_points_unomi — product catalogue mode
            if (rest.points["$fn"]) {
                return {
                    type,
                    params: { ...rest, points: "", _pointsMode: "dynamic", _dynamicPath: "productNames" },
                };
            }
            // $path: simple payload field (e.g. orderTotal)
            if (rest.points["$path"]) {
                const rawPath = rest.points["$path"];
                const displayPath = rawPath.replace(/^(transaction\.)?payload\./, "");
                return {
                    type,
                    params: { ...rest, points: "", _pointsMode: "dynamic", _dynamicPath: displayPath },
                };
            }
        }
        return { type, params: rest };
    });
}

// ── Remote select component ───────────────────────────────────────────────────
const RemoteSelect = ({ endpoint, valueField, labelField, brandName, value, onChange, label, required }) => {
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!brandName) return;
        setLoading(true);
        loyaltyFetch(`${LOYALTY_API_BASE}${endpoint}`, { headers: { "X-Brand": brandName } })
            .then(r => r.ok ? r.json() : {})
            .then(data => {
                const list = Array.isArray(data) ? data : (Array.isArray(data.items) ? data.items : []);
                setOptions(list);
            })
            .catch(() => setOptions([]))
            .finally(() => setLoading(false));
    }, [endpoint, brandName]);

    const selectedOption = options.find((opt) => String(opt[valueField]) === String(value || "")) || null;

    return (
        <Autocomplete
            options={options}
            loading={loading}
            value={selectedOption}
            isOptionEqualToValue={(option, current) => String(option?.[valueField]) === String(current?.[valueField])}
            getOptionLabel={(option) => option?.[labelField] || ""}
            onChange={(_, nextValue) => onChange(nextValue?.[valueField] || "")}
            noOptionsText={loading ? "Chargement..." : "Aucune option disponible"}
            renderOption={(props, option) => (
                <Box component="li" {...props}>
                    <Typography fontSize={13}>{option[labelField]}</Typography>
                    {option[valueField] !== option[labelField] && (
                        <Typography component="span" fontSize={11} color="text.secondary" ml={1}>
                            ({option[valueField]})
                        </Typography>
                    )}
                </Box>
            )}
            renderInput={(params) => (
                <TextField
                    {...params}
                    fullWidth
                    required={required}
                    size="small"
                    label={label}
                    helperText={loading ? "Chargement…" : `${options.length} option(s) disponible(s)`}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading ? <CircularProgress size={14} sx={{ mr: 1 }} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
};

// ── FreeSolo tag select (add_customer_tag) ────────────────────────────────────
const TagSelect = ({ brandName, value, onChange }) => {
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!brandName) return;
        setLoading(true);
        loyaltyFetch(`${LOYALTY_API_BASE}/admin/ui-options/customer-tags`, { headers: { "X-Brand": brandName } })
            .then(r => r.ok ? r.json() : {})
            .then(data => {
                const list = Array.isArray(data) ? data : (Array.isArray(data.items) ? data.items : []);
                setOptions(list.map(d => d.tag));
            })
            .catch(() => setOptions([]))
            .finally(() => setLoading(false));
    }, [brandName]);

    return (
        <Autocomplete freeSolo options={options} loading={loading}
            value={value || ""}
            onInputChange={(_, v) => onChange(v)}
            onChange={(_, v) => onChange(v || "")}
            renderInput={(params) => (
                <TextField {...params} required label="Tag client" size="small"
                    helperText="Saisissez un tag existant ou créez-en un nouveau"
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading && <CircularProgress size={14} />}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
};

const fsx = { "& .MuiOutlinedInput-root": { borderRadius: 2 } };
const normalizeSelectOption = (opt) => {
    if (opt && typeof opt === "object") {
        return {
            value: opt.value ?? "",
            label: opt.label ?? String(opt.value ?? ""),
        };
    }
    return { value: opt, label: String(opt) };
};

const IssueCouponFields = ({ params, setParam, setParams, brandName }) => {
    const [rewardOptions, setRewardOptions] = useState([]);
    const [loadingRewards, setLoadingRewards] = useState(false);
    const couponTypeId = params.coupon_type_id || "";
    const rewardMode = params._rewardMode || "all";

    useEffect(() => {
        if (!brandName || !couponTypeId) {
            setRewardOptions([]);
            return;
        }
        setLoadingRewards(true);
        loyaltyFetch(
            `${LOYALTY_API_BASE}/admin/ui-options/coupon-types/${couponTypeId}/rewards`,
            { headers: { "X-Brand": brandName } },
        )
            .then((r) => r.ok ? r.json() : [])
            .then((data) => {
                const list = Array.isArray(data) ? data : (Array.isArray(data?.items) ? data.items : []);
                setRewardOptions(list);
            })
            .catch(() => setRewardOptions([]))
            .finally(() => setLoadingRewards(false));
    }, [brandName, couponTypeId]);

    const selectedRewardIds = Array.isArray(params.reward_ids)
        ? params.reward_ids
        : (params.reward_ids ? [params.reward_ids] : []);

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <RemoteSelect
                    endpoint="/admin/ui-options/coupon-types"
                    valueField="id"
                    labelField="name"
                    brandName={brandName}
                    value={couponTypeId}
                    onChange={(v) => {
                        // Batch update avoids stale-state overwrite between consecutive setParam calls.
                        setParams({ coupon_type_id: v, reward_ids: [] });
                    }}
                    label="Type de coupon *"
                    required
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField fullWidth select label="Fréquence" size="small"
                    value={params.frequency ?? ""}
                    onChange={(e) => setParam("frequency", e.target.value)}
                    helperText="Optionnel"
                    sx={fsx}>
                    <MenuItem value=""><em>Par défaut</em></MenuItem>
                    <MenuItem value="ONCE_PER_CUSTOMER">Une fois par client</MenuItem>
                    <MenuItem value="ONCE_PER_CALENDAR_YEAR">Une fois par année civile</MenuItem>
                    <MenuItem value="ALWAYS">Toujours</MenuItem>
                </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField fullWidth select label="Récompenses incluses" size="small"
                    value={rewardMode}
                    onChange={(e) => {
                        const mode = e.target.value;
                        if (mode === "none") {
                            setParams({ _rewardMode: mode, reward_ids: [] });
                            return;
                        }
                        if (mode === "all") {
                            setParams({ _rewardMode: mode, reward_ids: undefined });
                            return;
                        }
                        setParams({ _rewardMode: mode });
                    }}
                    sx={fsx}>
                    <MenuItem value="all">Toutes les rewards actives du coupon</MenuItem>
                    <MenuItem value="pick">Liste stricte</MenuItem>
                    <MenuItem value="none">Coupon sans rewards</MenuItem>
                </TextField>
            </Grid>
            {rewardMode === "pick" && (
                <Grid item xs={12}>
                    <Autocomplete
                        multiple
                        options={rewardOptions}
                        loading={loadingRewards}
                        disabled={!couponTypeId}
                        value={rewardOptions.filter((o) => selectedRewardIds.includes(String(o.id)))}
                        isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
                        getOptionLabel={(o) => o?.name || String(o?.id || "")}
                        onChange={(_, vals) => setParam("reward_ids", vals.map((v) => v.id))}
                        renderInput={(inputParams) => (
                            <TextField
                                {...inputParams}
                                size="small"
                                label="Rewards à émettre"
                                helperText={!couponTypeId ? "Choisissez d’abord un type de coupon" : `${rewardOptions.length} reward(s) liée(s)`}
                                sx={fsx}
                            />
                        )}
                    />
                </Grid>
            )}
        </Grid>
    );
};

const ParamFields = ({ type, params, setParam, setParams, brandName, actionDef }) => {
    if (!type) return null;

    if (type === "issue_coupon") {
        return <IssueCouponFields params={params} setParam={setParam} setParams={setParams} brandName={brandName} />;
    }

    // ── earn_points has a custom UI (fixed / dynamic toggle) ──────────────────
    // Must be checked BEFORE the generic jsonSchema renderer below, because the
    // backend catalog may include a jsonSchema for earn_points that would
    // otherwise bypass our toggle entirely.
    if (type === "earn_points") {
        const mode = params._pointsMode || "fixed";
        return (
            <Grid container spacing={2}>
                {/* Mode toggle */}
                <Grid item xs={12}>
                    <Box display="flex" alignItems="center" gap={2} mb={0.5}>
                        <Typography fontSize={12} color="text.secondary" fontWeight={600}>
                            Mode points :
                        </Typography>
                        <ToggleButtonGroup
                            size="small"
                            exclusive
                            value={mode}
                            onChange={(_, val) => { if (val) setParam("_pointsMode", val); }}
                            sx={{ height: 30 }}
                        >
                            <ToggleButton value="fixed" sx={{ fontSize: 11, fontWeight: 700, px: 1.5, textTransform: "none", gap: 0.5 }}>
                                <PinIcon sx={{ fontSize: 14 }} /> Fixe
                            </ToggleButton>
                            <ToggleButton value="dynamic" sx={{ fontSize: 11, fontWeight: 700, px: 1.5, textTransform: "none", gap: 0.5 }}>
                                <DataObjectIcon sx={{ fontSize: 14 }} /> Dynamique
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                </Grid>

                {mode === "fixed" ? (
                    <>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="Points *" type="number" size="small" required
                                value={params.points ?? ""}
                                onChange={e => setParam("points", e.target.value)}
                                helperText="Nombre de points à attribuer"
                                InputProps={{ inputProps: { min: 0 } }} sx={fsx} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="Multiplier" type="number" size="small"
                                value={params.multiplier ?? ""}
                                onChange={e => setParam("multiplier", e.target.value)}
                                helperText="Ex: 2 = double les points (optionnel)"
                                InputProps={{ inputProps: { min: 1, step: 1 } }} sx={fsx} />
                        </Grid>
                    </>
                ) : (
                    <>
                        {/* ── Payload field selector ── */}
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth select label="Champ du payload" size="small"
                                value={params._dynamicPath ?? "orderTotal"}
                                onChange={e => setParam("_dynamicPath", e.target.value)}
                                helperText="Champ lu depuis transaction.payload"
                                sx={fsx}>
                                <MenuItem value="orderTotal">
                                    <Box>
                                        <Typography fontSize={13} fontWeight={600}>orderTotal</Typography>
                                        <Typography fontSize={10} color="text.secondary">Montant total de la transaction</Typography>
                                    </Box>
                                </MenuItem>
                                <MenuItem value="productNames">
                                    <Box>
                                        <Typography fontSize={13} fontWeight={600}>productNames + productQuantities</Typography>
                                        <Typography fontSize={10} color="text.secondary">Points par produit via Catalogue Produits</Typography>
                                    </Box>
                                </MenuItem>
                            </TextField>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="Multiplier" type="number" size="small"
                                value={params.multiplier ?? ""}
                                onChange={e => setParam("multiplier", e.target.value)}
                                helperText="Multiplicateur global appliqué après calcul (optionnel)"
                                InputProps={{ inputProps: { min: 1, step: 1 } }} sx={fsx} />
                        </Grid>

                    </>
                )}
            </Grid>
        );
    }

    if (actionDef && actionDef.jsonSchema) {
        const properties = actionDef.jsonSchema.properties || {};
        const requiredProps = actionDef.jsonSchema.required || [];
        const uiHints = actionDef.uiHints || {};

        const fields = Object.entries(properties).filter(([key]) => key !== 'type');

        if (fields.length === 0) {
            return (
                <Box px={2} py={1.5} sx={{ borderRadius: 2, bgcolor: "#f8fafc", border: "1px solid #e2e8f0" }}>
                    <Typography fontSize={12} color="text.secondary" fontStyle="italic">
                        Cette action ne nécessite aucun paramètre supplémentaire.
                    </Typography>
                </Box>
            );
        }

        return (
            <Grid container spacing={2}>
                {fields.map(([key, schema]) => {
                    const hint = uiHints[key] || {};
                    const isRequired = requiredProps.includes(key);
                    const title = schema.title || key;

                    if (hint.widget === "remote_select") {
                        return (
                            <Grid item xs={12} sm={fields.length > 1 ? 6 : 12} key={key}>
                                <RemoteSelect
                                    endpoint={hint.datasource.endpoint}
                                    valueField={hint.datasource.valueField}
                                    labelField={hint.datasource.labelField}
                                    brandName={brandName}
                                    value={params[key] ?? ""}
                                    onChange={v => setParam(key, v)}
                                    label={title}
                                    required={isRequired}
                                />
                            </Grid>
                        );
                    }
                    
                    if (hint.widget === "tag_select") {
                        return (
                            <Grid item xs={12} sm={fields.length > 1 ? 6 : 12} key={key}>
                                <TagSelect
                                    brandName={brandName}
                                    value={params[key] ?? ""}
                                    onChange={v => setParam(key, v)}
                                />
                            </Grid>
                        );
                    }

                    const isNumber = hint.widget === "number" || 
                                     schema.type === "integer" || 
                                     schema.type === "number" || 
                                     (schema.anyOf && schema.anyOf.some(s => s.type === "integer" || s.type === "number"));

                    if (isNumber) {
                        return (
                            <Grid item xs={12} sm={fields.length > 1 ? 6 : 12} key={key}>
                                <TextField
                                    fullWidth
                                    label={title}
                                    type="number"
                                    size="small"
                                    required={isRequired}
                                    value={params[key] ?? ""}
                                    onChange={e => setParam(key, e.target.value)}
                                    placeholder={hint.placeholder}
                                    InputProps={{ inputProps: { min: hint.min } }}
                                    sx={fsx}
                                />
                            </Grid>
                        );
                    }

                    if (hint.widget === "select" || schema.enum) {
                        const options = (hint.options || schema.enum || []).map(normalizeSelectOption);
                        return (
                            <Grid item xs={12} sm={fields.length > 1 ? 6 : 12} key={key}>
                                <TextField 
                                    fullWidth select required={isRequired} label={title} size="small"
                                    value={params[key] ?? ""}
                                    onChange={e => setParam(key, e.target.value)}
                                    sx={fsx}
                                >
                                    {options.map(opt => (
                                        <MenuItem key={String(opt.value)} value={opt.value}>{opt.label}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                        );
                    }

                    return (
                        <Grid item xs={12} sm={fields.length > 1 ? 6 : 12} key={key}>
                            <TextField
                                fullWidth
                                label={title}
                                size="small"
                                required={isRequired}
                                value={params[key] ?? ""}
                                onChange={e => setParam(key, e.target.value)}
                                placeholder={hint.placeholder}
                                sx={fsx}
                            />
                        </Grid>
                    );
                })}
            </Grid>
        );
    }

    switch (type) {
        case "earn_points": {
            const mode = params._pointsMode || "fixed";
            return (
                <Grid container spacing={2}>
                    {/* Mode toggle — only for earn_points */}
                    <Grid item xs={12}>
                        <Box display="flex" alignItems="center" gap={2} mb={0.5}>
                            <Typography fontSize={12} color="text.secondary" fontWeight={600}>
                                Mode points :
                            </Typography>
                            <ToggleButtonGroup
                                size="small"
                                exclusive
                                value={mode}
                                onChange={(_, val) => { if (val) setParam("_pointsMode", val); }}
                                sx={{ height: 30 }}
                            >
                                <ToggleButton value="fixed" sx={{ fontSize: 11, fontWeight: 700, px: 1.5, textTransform: "none", gap: 0.5 }}>
                                    <PinIcon sx={{ fontSize: 14 }} /> Fixe
                                </ToggleButton>
                                <ToggleButton value="dynamic" sx={{ fontSize: 11, fontWeight: 700, px: 1.5, textTransform: "none", gap: 0.5 }}>
                                    <DataObjectIcon sx={{ fontSize: 14 }} /> Dynamique
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                    </Grid>

                    {mode === "fixed" ? (
                        <>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth label="Points *" type="number" size="small" required
                                    value={params.points ?? ""}
                                    onChange={e => setParam("points", e.target.value)}
                                    helperText="Nombre de points à attribuer"
                                    InputProps={{ inputProps: { min: 0 } }} sx={fsx} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth label="Multiplier" type="number" size="small"
                                    value={params.multiplier ?? ""}
                                    onChange={e => setParam("multiplier", e.target.value)}
                                    helperText="Ex: 2 = double les points (optionnel)"
                                    InputProps={{ inputProps: { min: 1, step: 1 } }} sx={fsx} />
                            </Grid>
                        </>
                    ) : (
                        <>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth label="Champ du payload (orderTotal)" size="small" required
                                    value={params._dynamicPath ?? ""}
                                    onChange={e => setParam("_dynamicPath", e.target.value)}
                                    placeholder="orderTotal"
                                    helperText={`Chemin dans transaction.payload — ex: orderTotal → payload.orderTotal`}
                                    sx={fsx} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth label="Multiplier" type="number" size="small"
                                    value={params.multiplier ?? ""}
                                    onChange={e => setParam("multiplier", e.target.value)}
                                    helperText="Ex: 1 point par unité (optionnel)"
                                    InputProps={{ inputProps: { min: 1, step: 1 } }} sx={fsx} />
                            </Grid>
                        </>
                    )}
                </Grid>
            );
        }

        case "burn_points":
            return (
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField fullWidth label="Points" type="number" size="small" required
                            value={params.points ?? ""}
                            onChange={e => setParam("points", e.target.value)}
                            helperText="Nombre de points à déduire"
                            InputProps={{ inputProps: { min: 0 } }} sx={fsx} />
                    </Grid>
                </Grid>
            );

        case "redeem_reward":
        case "issue_reward":
            return (
                <RemoteSelect
                    endpoint="/admin/ui-options/rewards"
                    valueField="id" labelField="name"
                    brandName={brandName}
                    value={params.reward_id ?? ""}
                    onChange={v => setParam("reward_id", v)}
                    label="Récompense (reward_id)"
                    required
                />
            );

        case "earn_points_from_amount":
            return (
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                        <TextField fullWidth label="Taux (rate)" type="number" size="small" required
                            value={params.rate ?? ""}
                            onChange={e => setParam("rate", e.target.value)}
                            helperText="Ex: 1 = 1 point par unité de montant"
                            InputProps={{ inputProps: { step: "0.01", min: 0 } }} sx={fsx} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField fullWidth label="min_points" type="number" size="small" required
                            value={params.min_points ?? ""}
                            onChange={e => setParam("min_points", e.target.value)}
                            helperText="Nombre minimum de points attribués"
                            InputProps={{ inputProps: { min: 0 } }} sx={fsx} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField fullWidth label="max_points" type="number" size="small" required
                            value={params.max_points ?? ""}
                            onChange={e => setParam("max_points", e.target.value)}
                            helperText="Nombre maximum de points attribués"
                            InputProps={{ inputProps: { min: 0 } }} sx={fsx} />
                    </Grid>
                </Grid>
            );



        case "reset_status_points":
        case "downgrade_one_tier":
            return (
                <Box px={2} py={1.5} sx={{ borderRadius: 2, bgcolor: "#f8fafc", border: "1px solid #e2e8f0" }}>
                    <Typography fontSize={12} color="text.secondary" fontStyle="italic">
                        Cette action ne nécessite aucun paramètre supplémentaire.
                    </Typography>
                </Box>
            );

        case "set_customer_status":
            return (
                <TextField fullWidth select required label="Statut client (status)" size="small"
                    value={params.status ?? ""}
                    onChange={e => setParam("status", e.target.value)}
                    helperText='Ex: VIP, ACTIVE, INACTIVE, BANNED'
                    sx={fsx}>
                    <MenuItem value="ACTIVE">ACTIVE</MenuItem>
                    <MenuItem value="INACTIVE">INACTIVE</MenuItem>
                    <MenuItem value="VIP">VIP</MenuItem>
                    <MenuItem value="BANNED">BANNED</MenuItem>
                </TextField>
            );

        case "add_customer_tag":
            return (
                <TagSelect
                    brandName={brandName}
                    value={params.tag ?? ""}
                    onChange={v => setParam("tag", v)}
                />
            );

        default:
            return (
                <Typography fontSize={12} color="text.secondary">
                    Type d'action non reconnu : {type}
                </Typography>
            );
    }
};

const ActionRow = ({ action, actionCatalog, brandName, onUpdate, onDelete }) => {
    const type = action?.type || "";
    const params = action?.params || {};
    const meta = ACTION_META[type] || {};

    const setType = (newType) => {
        if (!newType) return;
        onUpdate({ type: newType, params: {} });
    };

    const setParam = (key, val) => {
        onUpdate({ type, params: { ...params, [key]: val } });
    };
    const setParams = (patch) => {
        onUpdate({ type, params: { ...params, ...patch } });
    };

    return (
        <Box sx={{ p: 2, bgcolor: "#fafbfd", border: "1px solid #e8ecf1", borderRadius: 2, position: "relative" }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Box display="flex" alignItems="center" gap={1}>
                    <FlashOnIcon sx={{ fontSize: 18, color: SECTION_COLORS.rules.main }} />
                    <Typography fontSize={13} fontWeight={700} color="#334155">Action</Typography>
                    {type && meta.label && (
                        <Chip label={meta.label} size="small"
                            sx={{ fontSize: 10, fontWeight: 700, bgcolor: meta.bg, color: meta.color }} />
                    )}
                </Box>
                <IconButton size="small" onClick={onDelete} sx={{ color: "#ef4444" }}>
                    <DeleteIcon fontSize="small" />
                </IconButton>
            </Box>

            <TextField fullWidth select size="small" label="Type d'action" value={type}
                onChange={e => setType(e.target.value)} required
                sx={{ ...fsx, mb: type ? 2 : 0 }}>
                <MenuItem value="" disabled><em>— Choisir une action —</em></MenuItem>
                {actionCatalog.map(a => {
                    const m = ACTION_META[a.type] || {};
                    return (
                        <MenuItem key={a.type} value={a.type}>
                            <Box display="flex" alignItems="center" gap={1}>
                                <Chip label={m.label || a.title || a.type} size="small"
                                    sx={{
                                        fontSize: 10, fontWeight: 700, pointerEvents: "none",
                                        bgcolor: m.bg || "#f1f5f9", color: m.color || "#475569"
                                    }} />
                            </Box>
                        </MenuItem>
                    );
                })}
            </TextField>

            {type && (
                <ParamFields
                    type={type}
                    params={params}
                    setParam={setParam}
                    setParams={setParams}
                    brandName={brandName}
                    actionDef={actionCatalog.find(a => a.type === type)}
                />
            )}
        </Box>
    );
};

const ActionBuilder = ({ brandName, actionCatalog, value, onChange }) => {
    // value = Array of { type, params: {} }
    const actions = Array.isArray(value) ? value : [];

    const addAction = () => {
        onChange([...actions, { type: "", params: {} }]);
    };

    const updateAction = (index, updatedAction) => {
        const next = [...actions];
        next[index] = updatedAction;
        onChange(next);
    };

    const deleteAction = (index) => {
        const next = actions.filter((_, i) => i !== index);
        onChange(next);
    };

    return (
        <Box>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                <Typography fontSize={13} fontWeight={700} color="#334155">Actions</Typography>
                <Button
                    startIcon={<AddIcon />}
                    size="small"
                    onClick={addAction}
                    sx={{ textTransform: "none", fontWeight: 600, color: SECTION_COLORS.rules.main }}
                >
                    Ajouter une action
                </Button>
            </Box>

            <Stack spacing={2}>
                {actions.length === 0 && (
                    <Box px={2} py={3} sx={{ borderRadius: 2, border: "1px dashed #cbd5e1", textAlign: "center" }}>
                        <Typography fontSize={12} color="text.secondary">
                            Aucune action définie. Cette règle ne fera rien.
                        </Typography>
                    </Box>
                )}
                {actions.map((action, idx) => (
                    <ActionRow
                        key={idx}
                        action={action}
                        actionCatalog={actionCatalog}
                        brandName={brandName}
                        onUpdate={(updated) => updateAction(idx, updated)}
                        onDelete={() => deleteAction(idx)}
                    />
                ))}
            </Stack>
        </Box>
    );
};

export default ActionBuilder;
